import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Upload,
  Button,
  Modal,
  message,
  Divider,
  Row,
  Col,
  Space,
  Alert,
} from "antd";
import {
  CameraOutlined,
  UploadOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Api_Url } from "../../../config/config";

const { Option } = Select;
const { TextArea } = Input;

export const GeneralModalAdd = ({ onClose, GeneralDataFetchFunc }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selfie, setSelfie] = useState(null);
  const [siteImages, setSiteImages] = useState([]);
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [customerSearchMode, setCustomerSearchMode] = useState(true);
  const [searchAttempted, setSearchAttempted] = useState(false);

  // Visit type options
  const visitTypes = [
    { value: "Business PD", label: "Business PD" },
    { value: "Residence PD", label: "Residence PD" },
    { value: "Others", label: "Others" },
  ];

  useEffect(() => {
    fetchBranches();
    getGeolocation();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await fetch(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        }
      );
      const data = await response.json();
      setBranches(data.data);
    } catch (error) {
      message.error("Failed to load branches");
    }
  };

  const getGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          message.warning(
            "Geolocation permission denied. Some features may be limited."
          );
        }
      );
    }
  };

  const searchCustomers = async () => {
    const branchId = form.getFieldValue("BranchId");
    const searchTerm = form.getFieldValue("customerSearch");

    if (!branchId) {
      message.warning("Please select a branch first");
      return;
    }

    if (!searchTerm || searchTerm.length < 3) {
      message.warning("Please enter at least 3 characters to search");
      return;
    }

    setSearchAttempted(true);
    setSearching(true);
    try {
      const response = await fetch(
        `${Api_Url}api/v1/Customer/bybranchidandname/${branchId}/${searchTerm}`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        }
      );
      const data = await response.json();
      setCustomers(data.data || []);
    } catch (error) {
      message.error("Failed to search customers");
    } finally {
      setSearching(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    form.setFieldsValue({
      Name: customer.name,
      ApplicationNo: customer.applicationNo,
      ResidenceAddress: customer.address || "",
    });
    setCustomers([]);
    setCustomerSearchMode(false);
  };

  const enableManualEntry = () => {
    form.setFieldsValue({
      customerSearch: "",
      Name: "",
      ApplicationNo: "",
      ResidenceAddress: "",
    });
    setCustomerSearchMode(false);
    setCustomers([]);
  };

  const handleSelfieUpload = (info) => {
    if (info.file.status === "removed") {
      setSelfie(null);
      return;
    }
    const file = info.file.originFileObj || info.file;
    setSelfie(file);
  };

  const handleSiteImagesUpload = (info) => {
    let fileList = [...info.fileList];
    fileList = fileList.slice(-5);
    fileList = fileList.map((file) => {
      if (file.response) {
        file.url = file.response.url;
      }
      return file;
    });
    setSiteImages(fileList.filter((file) => file.originFileObj || file.url));
  };

  const removeSiteImage = (index) => {
    const newImages = [...siteImages];
    newImages.splice(index, 1);
    setSiteImages(newImages);
  };

  const onFinish = async (values) => {
    if (!selfie) {
      message.error("Please upload a selfie image");
      return;
    }
    if (siteImages.length === 0) {
      message.error("Please upload at least one site image");
      return;
    }

    setLoading(true);
    const formData = new FormData();

    // --- CORRECTED FORM DATA APPENDING ---
    // Append fields explicitly with correct keys to match the backend model
    formData.append("BranchId", values.BranchId);
    formData.append("Name", values.Name);
    formData.append("ApplicationNo", values.ApplicationNo || "");
    formData.append("VisitType", values.visitType);
    formData.append("ResidenceAddress", values.ResidenceAddress || "");
    formData.append("Remarks", values.remarks);
    formData.append("Latitude", location.lat);
    formData.append("Longitude", location.lng);
    formData.append("PanNumber", values.PanNumber || "");
    formData.append("DistanceCovered", "0"); // Add missing field with a default value

    // Conditional fields
    if (values.visitType === "Others") {
      formData.append("VisitTypeDescription", values.visitTypeDescription || "");
    } else if (values.visitType === "Business PD") {
      formData.append("ProfileType", values.profileType || "");
    }

    // Append selfie image
    formData.append("Selfie", selfie);

    // Append all site images with the same field name "SiteImages"
    siteImages.forEach((image) => {
      formData.append("SiteImages", image.originFileObj || image);
    });

    try {
      const response = await fetch(`${Api_Url}api/v1/GeneralPd`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user")).accessToken
          }`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Submission failed:", errorData);
        throw new Error(errorData.message || "Submission failed");
      }

      message.success("General PD submitted successfully");
      GeneralDataFetchFunc();
      onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: "20px", fontWeight: "bold" }}>
          Add General PD
        </span>
      }
      width={900}
      visible={true}
      onCancel={onClose}
      footer={[
        <Space key="footer-buttons" size="large">
          <Button
            key="back"
            onClick={onClose}
            size="large"
            style={{ width: 120 }}
          >
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            size="large"
            style={{ width: 120 }}
          >
            Submit
          </Button>
        </Space>,
      ]}
      centered
      bodyStyle={{ padding: "24px" }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ visitType: "Business PD" }}
      >
        <Divider orientation="left" style={{ marginTop: 0 }}>
          Branch Information
        </Divider>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="BranchId"
              label={<span style={{ fontWeight: 500 }}>Branch</span>}
              rules={[{ required: true, message: "Please select branch" }]}
            >
              <Select
                placeholder="Select branch"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                size="large"
                onChange={() => {
                  setCustomerSearchMode(true);
                  setSearchAttempted(false);
                  setCustomers([]);
                  form.setFieldsValue({
                    customerSearch: "",
                    Name: "",
                    ApplicationNo: "",
                    ResidenceAddress: "",
                  });
                }}
              >
                {branches.map((branch) => (
                  <Option key={branch.id} value={branch.id}>
                    {branch.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Customer Details</Divider>

        {customerSearchMode ? (
          <>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  name="customerSearch"
                  label={
                    <span style={{ fontWeight: 500 }}>
                      Search Customer (Hit Enter for Search After Typing)
                    </span>
                  }
                >
                  <Input
                    placeholder="Search customer by name (min 3 characters)"
                    size="large"
                    suffix={
                      <Button
                        type="text"
                        icon={<SearchOutlined />}
                        onClick={searchCustomers}
                        loading={searching}
                      />
                    }
                    onPressEnter={searchCustomers}
                  />
                </Form.Item>
              </Col>
            </Row>

            {searchAttempted && !searching && customers.length === 0 && (
              <Alert
                message="Customer not found"
                description={
                  <div>
                    <p>
                      No matching customers found. Would you like to enter the
                      details manually?
                    </p>
                    <Button type="primary" onClick={enableManualEntry}>
                      Enter Manually
                    </Button>
                  </div>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {customers.length > 0 && (
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  marginBottom: 16,
                }}
              >
                {customers.map((customer) => (
                  <div
                    key={customer.id}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f0f0f0",
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "")
                    }
                    onClick={() => handleCustomerSelect(customer)}
                  >
                    {customer.name} ({customer.applicationNo})
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <Row gutter={24}>
              <Col span={24}>
                <Button
                  type="dashed"
                  onClick={() => setCustomerSearchMode(true)}
                  style={{ marginBottom: 16 }}
                >
                  Search for Existing Customer
                </Button>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                {/* --- CORRECTED FIELD NAME --- */}
                <Form.Item
                  name="Name"
                  label={<span style={{ fontWeight: 500 }}>Customer Name</span>}
                  rules={[
                    { required: true, message: "Please enter customer name" },
                  ]}
                >
                  <Input placeholder="Customer name" size="large" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="ApplicationNo"
                  label={
                    <span style={{ fontWeight: 500 }}>Application Number</span>
                  }
                >
                  <Input placeholder="Application number" size="large" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <Row gutter={24}>
          <Col span={12}>
            {/* --- CORRECTED FIELD NAME --- */}
            <Form.Item
              name="PanNumber"
              label={<span style={{ fontWeight: 500 }}>PAN Number</span>}
            >
              <Input
                placeholder="PAN number (for pre-login cases)"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="visitType"
              label={<span style={{ fontWeight: 500 }}>Visit Type</span>}
              rules={[{ required: true, message: "Please select visit type" }]}
            >
              <Select size="large">
                {visitTypes.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        
        {/* The conditional rendering for these fields is now handled in onFinish */}
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.visitType !== currentValues.visitType
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("visitType") === "Others" ? (
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    name="visitTypeDescription"
                    label={<span style={{ fontWeight: 500 }}>Visit Description</span>}
                  >
                    <TextArea
                      rows={3}
                      placeholder="Describe the purpose of visit"
                      size="large"
                      style={{ resize: "vertical" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            ) : null
          }
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.visitType !== currentValues.visitType
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("visitType") === "Business PD" ? (
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    name="profileType"
                    label={<span style={{ fontWeight: 500 }}>Profile Type</span>}
                  >
                    <Input
                      placeholder="Enter profile type (e.g., Salaried, Business, etc.)"
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
            ) : null
          }
        </Form.Item>

        <Divider orientation="left">Address Details</Divider>
        <Row gutter={24}>
          <Col span={24}>
            {/* --- CORRECTED FIELD NAME --- */}
            <Form.Item
              name="ResidenceAddress"
              label={<span style={{ fontWeight: 500 }}>Customer Address</span>}
            >
              <TextArea
                rows={3}
                placeholder="Full customer address"
                size="large"
                style={{ resize: "vertical" }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="remarks"
              label={<span style={{ fontWeight: 500 }}>Remarks</span>}
              rules={[{ required: true, message: "Please enter remarks" }]}
            >
              <TextArea
                rows={4}
                placeholder="Detailed remarks about the visit"
                size="large"
                style={{ resize: "vertical" }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Documentation</Divider>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Selfie Image</span>}
              required
              style={{ marginBottom: 0 }}
            >
              <div style={{ marginBottom: 8, color: "rgba(0, 0, 0, 0.45)" }}>
                Upload your selfie photo
              </div>
              <Upload
                accept="image/*"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleSelfieUpload}
                style={{ width: "100%" }}
              >
                {selfie ? (
                  <img
                    src={
                      selfie instanceof File
                        ? URL.createObjectURL(selfie)
                        : selfie
                    }
                    alt="selfie"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <CameraOutlined
                      style={{ fontSize: "32px", color: "#1890ff" }}
                    />
                    <div style={{ marginTop: 8 }}>Click to Upload</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Site Images</span>}
              required
              style={{ marginBottom: 0 }}
            >
              <div style={{ marginBottom: 8, color: "rgba(0, 0, 0, 0.45)" }}>
                Upload at least one site photo (max 5)
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {siteImages.map((file, index) => {
                  const imageUrl = file.originFileObj
                    ? URL.createObjectURL(file.originFileObj)
                    : file.url;

                  return (
                    <div
                      key={file.uid || index}
                      style={{
                        position: "relative",
                        width: 100,
                        height: 100,
                        borderRadius: 8,
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={imageUrl}
                        alt={`site-${index}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Button
                        icon={<DeleteOutlined />}
                        shape="circle"
                        size="small"
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "#ff4d4f",
                          color: "white",
                          border: "none",
                        }}
                        onClick={() => removeSiteImage(index)}
                      />
                    </div>
                  );
                })}
                {siteImages.length < 5 && (
                  <Upload
                    accept="image/*"
                    listType="picture-card"
                    multiple
                    beforeUpload={() => false}
                    onChange={handleSiteImagesUpload}
                    fileList={siteImages}
                    showUploadList={false}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <UploadOutlined
                        style={{ fontSize: "24px", color: "#1890ff" }}
                      />
                      <div style={{ marginTop: 4 }}>Add</div>
                    </div>
                  </Upload>
                )}
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};